// ABOUTME: Rich-text form field backed by Quill (react-quill), with an explicit edit/save/cancel mode toggle.
// ABOUTME: Prevents the node save button from enabling while the editor is in editing mode via hasUnsavedHtmlFields.
import 'react-quill/dist/quill.snow.css';

import { Box, CheckIcon, CloseIcon, EditIcon, FormControl, IconButton, Stack } from '@ds/coolshop';
import { FieldProps } from '@src/assets/types';
import { useHtmlFieldEditing } from '@src/components/hierarchies/shared/HtmlFieldEditingContext';
import InputLabelC from '@src/components/shared/InputLabelC';
import SnackBarUtils from '@src/components/shared/SnackBarUtils';
import { SettingsContext } from '@src/modules/setting/SettingsContext';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useController } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import ReactQuill, { Quill } from 'react-quill';

import { enhanceContent } from './HtmlFieldElement/api';

// Register custom AI enhancement icon
const icons = Quill.import('ui/icons');
icons['ai-enhance'] = '✨';

const BASE_TOOLBAR = [
    ['bold', 'italic', 'underline', 'strike'],
    [{ header: 1 }, { header: 2 }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ script: 'sub' }, { script: 'super' }],
    [{ direction: 'rtl' }],
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    [{ color: [] }, { background: [] }],
    ['clean'],
];

const HtmlFieldElement = ({
    name,
    control,
    label,
    isMultiLanguage,
    position,
    readOnly,
    disabled,
    LanguageComponent,
    onChange = () => {},
}: FieldProps['html']): JSX.Element => {
    const { field } = useController({
        name,
        control,
    });

    const reactQuillRef = useRef<ReactQuill>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [localValue, setLocalValue] = useState(field.value || '');
    const [isEditing, setIsEditing] = useState(false);
    const originalValueRef = useRef<string>(field.value || '');
    const isDisabled = useMemo(() => disabled || readOnly, [disabled, readOnly]);
    const { enableAI } = useContext(SettingsContext);
    const { setFieldEditing, registerField, unregisterField } = useHtmlFieldEditing();
    const fieldIdRef = useRef<string>(name);

    // Build toolbar with conditional AI button
    const toolbar = useMemo(
        () => (enableAI ? [...BASE_TOOLBAR, ['ai-enhance']] : BASE_TOOLBAR),
        [enableAI],
    );

    useEffect(() => {
        if (!reactQuillRef.current) return;
        const quillInstance = reactQuillRef.current.getEditor();
        const toolbarModule = quillInstance.getModule('toolbar');
        toolbarModule.container.style.zIndex = position ? `9${position}` : '9';
        toolbarModule.container.style.position = 'relative';
    }, [position]);

    // Disable toolbar buttons when field is disabled or not in edit mode
    useEffect(() => {
        if (!reactQuillRef.current) return;
        const quillInstance = reactQuillRef.current.getEditor();
        const toolbarModule = quillInstance.getModule('toolbar');
        const toolbarContainer = toolbarModule.container;

        const shouldDisable = isDisabled || !isEditing;

        if (shouldDisable) {
            // Disable all toolbar interactive elements
            toolbarContainer.style.pointerEvents = 'none';
        } else {
            // Re-enable toolbar interactive elements
            toolbarContainer.style.pointerEvents = 'auto';
        }
    }, [isDisabled, isEditing]);

    // Register field with context on mount
    useEffect(() => {
        if (containerRef.current) {
            registerField(fieldIdRef.current, label, containerRef as React.RefObject<HTMLElement>);
        }
        // Cleanup: unregister field on unmount
        return () => {
            unregisterField(fieldIdRef.current);
        };
    }, [label, name, registerField, unregisterField]);

    // Track editing state in context
    useEffect(() => {
        setFieldEditing(fieldIdRef.current, isEditing);

        // Cleanup: remove field from editing state on unmount
        return () => {
            setFieldEditing(fieldIdRef.current, false);
        };
    }, [isEditing, setFieldEditing]);

    // Sync local value with field value when it changes externally (e.g., form reset)
    // Don't sync when editing to preserve user's local changes
    useEffect(() => {
        if (isEditing) return; // Don't sync external changes while editing

        const currentFieldValue = field.value || '';
        // Only sync if field value changed externally (different from local value)
        if (currentFieldValue !== localValue) {
            setLocalValue(currentFieldValue);
            originalValueRef.current = currentFieldValue;
        }
    }, [field.value, localValue, isEditing]);

    function normalizeHtml(htmlString: string) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlString, 'text/html');

        doc.querySelectorAll('[style]').forEach(element => {
            if (element instanceof HTMLElement) {
                const styles = Array.from(element.style);
                const sortedStyles = styles
                    // remove transparent background style causing spacing issues (PIM-534)
                    .filter(
                        prop =>
                            prop !== 'background-color' &&
                            element.style.getPropertyValue(prop) !== 'transparent',
                    )
                    .sort()
                    .map(prop => `${prop}: ${element.style.getPropertyValue(prop)};`)
                    .join(' ');
                element.setAttribute('style', sortedStyles);
            }
        });

        return doc.body.innerHTML;
    }

    const handleAIEnhance = useCallback(async () => {
        if (!reactQuillRef.current || isProcessing) return;

        try {
            setIsProcessing(true);

            // Get current content from Quill editor
            const quillInstance = reactQuillRef.current.getEditor();
            const currentContent = quillInstance.root.innerHTML;

            // Call the enhancement API
            const enhancedContent = await enhanceContent(currentContent);

            // Normalize and update the content
            const normalizedContent = normalizeHtml(enhancedContent);
            const fieldValue = normalizedContent !== '<p><br></p>' ? normalizedContent : '';

            // Update local state immediately
            setLocalValue(fieldValue);

            // Update via React Hook Form to preserve undo history
            field.onChange(fieldValue);
            onChange(fieldValue);
        } catch (error) {
            SnackBarUtils.error(
                error instanceof Error ? error.message : 'Failed to enhance content',
            );
        } finally {
            setIsProcessing(false);
        }
    }, []);

    const handleTextChange = (content: string, _delta: any, source: string) => {
        if (source === 'user') {
            const normalizedContent = normalizeHtml(content);
            const fieldValue = normalizedContent !== '<p><br></p>' ? normalizedContent : '';

            // Update local state immediately for lag-free typing
            setLocalValue(fieldValue);

            // Only update form value if not in edit mode
            // When editing, changes are only local until Save is clicked
            if (!isEditing) {
                field.onChange(fieldValue);
                onChange(fieldValue);
            }
        }
    };

    const handleEdit = useCallback(() => {
        // Store the current form value as the original
        const currentFieldValue = field.value || '';
        originalValueRef.current = currentFieldValue;
        // Ensure local value is synced with field value when starting to edit
        setLocalValue(currentFieldValue);
        setIsEditing(true);
    }, [field.value]);

    const handleSave = useCallback(() => {
        // Normalize the current local value
        const normalizedContent = normalizeHtml(localValue);
        const fieldValue = normalizedContent !== '<p><br></p>' ? normalizedContent : '';

        // Update form value
        field.onChange(fieldValue);
        onChange(fieldValue);

        // Exit edit mode
        setIsEditing(false);
        originalValueRef.current = fieldValue;
    }, [localValue, field, onChange]);

    const handleCancel = useCallback(() => {
        // Revert to original form value
        const originalValue = originalValueRef.current;
        setLocalValue(originalValue);
        setIsEditing(false);
    }, []);

    const modules = useMemo(
        () => ({
            toolbar: {
                container: toolbar,
                handlers: enableAI
                    ? {
                          'ai-enhance': handleAIEnhance,
                      }
                    : undefined,
            },
            clipboard: { matchVisual: false },
        }),
        [toolbar, enableAI, handleAIEnhance],
    );

    return (
        <Box
            ref={containerRef}
            data-testid={`html-field-${name}`}
            bgcolor="background.default"
            borderRadius={2}
            position="relative"
        >
            <InputLabelC
                id="select-field-label"
                sx={theme => ({
                    position: 'absolute',
                    zIndex: 10,
                    top: 40,
                    left: 16,
                    fontSize: '0.75rem',
                    paddingInline: 0.5,
                    backgroundColor: theme.palette.background.default,
                })}
                data-testid="select-field-label"
            >
                <FormattedMessage
                    defaultMessage={!isMultiLanguage ? label : ' '}
                    id="select-field-label"
                />
            </InputLabelC>
            <FormControl fullWidth>
                <Stack gap={1} justifyContent="center" alignItems="flex-start">
                    {LanguageComponent}
                    <Box
                        width="100%"
                        minHeight="fit-content"
                        position="relative"
                        pointerEvents={isDisabled ? 'none' : 'auto'}
                        border={isEditing ? '2px dashed red' : '2px dashed'}
                        borderColor={isEditing ? undefined : 'divider'}
                        borderRadius={2}
                        p={1}
                    >
                        <Stack direction="row" gap={0.5} justifyContent="flex-end" mb={1}>
                            {!isEditing ? (
                                <IconButton
                                    size="small"
                                    onClick={handleEdit}
                                    disabled={isDisabled}
                                    color="primary"
                                >
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            ) : (
                                <>
                                    <IconButton
                                        size="small"
                                        onClick={handleCancel}
                                        disabled={isProcessing}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={handleSave}
                                        disabled={isProcessing}
                                        color="primary"
                                    >
                                        <CheckIcon fontSize="small" />
                                    </IconButton>
                                </>
                            )}
                        </Stack>
                        <ReactQuill
                            ref={reactQuillRef}
                            value={localValue}
                            onChange={handleTextChange}
                            modules={modules}
                            theme="snow"
                            readOnly={isDisabled || isProcessing || !isEditing}
                        />
                    </Box>
                </Stack>
            </FormControl>
        </Box>
    );
};

export default HtmlFieldElement;
